import React from 'react';
import { Card, CardActionArea, CardContent, Stack, Typography, Chip, Button, Box } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link as RouterLink } from 'react-router-dom';

export type VoteCardProps = {
  date: string;
  billId: string; // e.g., HRES 682
  voteType: string; // e.g., 2/3 Yea-and-Nay
  result: string; // Passed / Failed
  rollCall?: string; // e.g., 241
  billUrl?: string; // /bills/:congress/:type/:num
};

// Utility to format a date string into, e.g., Feb 25, 2025
function formatDate(d: string): string {
  try {
    const dt = new Date(d);
    if (!isFinite(dt.getTime())) return d;
    return dt.toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return d;
  }
}

const outcomeColor = (result: string | undefined) => {
  const r = (result || '').toLowerCase();
  if (r.includes('pass')) return '#22c55e'; // green
  if (r.includes('fail')) return '#ef4444'; // red
  return undefined;
};

const typeColor: Record<string, 'default' | 'info' | 'warning' | 'primary' | 'secondary' | 'success' | 'error'> = {
  'yea-and-nay': 'info',
  '2/3 yea-and-nay': 'warning',
};

function pickTypeColor(voteType: string | undefined) {
  const t = (voteType || '').toLowerCase();
  if (t.includes('2/3')) return 'warning';
  if (t.includes('yea-and-nay')) return 'info';
  return 'default';
}

export const VoteCard: React.FC<VoteCardProps> = ({ date, billId, voteType, result, rollCall, billUrl }) => {
  const passed = (result || '').toLowerCase().includes('pass');
  const failed = (result || '').toLowerCase().includes('fail');
  const color = outcomeColor(result);

  const content = (
    <CardContent>
      <Stack spacing={1}>
        {/* Date */}
        {date && (
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
            {formatDate(date)}
          </Typography>
        )}
        {/* Bill + Type row */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {billId}
          </Typography>
          {voteType && (
            <Chip size="small" label={voteType} color={pickTypeColor(voteType)} variant="outlined" />
          )}
        </Stack>
        {/* Outcome + Roll call */}
        <Stack direction="row" spacing={1} alignItems="center">
          {passed && <CheckCircleIcon sx={{ color, fontSize: 20 }} />}
          {failed && <CancelIcon sx={{ color, fontSize: 20 }} />}
          <Typography variant="body2" sx={{ color, fontWeight: 600 }}>
            {result}
          </Typography>
          {rollCall && (
            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
              · Roll Call {rollCall}
            </Typography>
          )}
        </Stack>
        {/* CTA */}
        {billUrl && (
          <Box>
            <Button size="small" endIcon={<ChevronRightIcon />} component={RouterLink as any} to={billUrl} variant="contained">
              View Bill
            </Button>
          </Box>
        )}
      </Stack>
    </CardContent>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(30,41,59,0.7)' : 'rgba(241,245,249,0.6)',
        transition: 'background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(51,65,85,0.9)' : 'rgba(226,232,240,0.9)',
        },
      }}
    >
      {billUrl ? (
        <CardActionArea component={RouterLink as any} to={billUrl}>
          {content}
        </CardActionArea>
      ) : content}
    </Card>
  );
};

export default VoteCard;
